import { mdiClose, mdiDatabase, mdiFileDocument, mdiUpload } from '@mdi/js';
import { Icon } from '@mdi/react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Dialog, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { type OperationStatus, useNotificationManager } from '@qre-mailing/smart-table';
import { toast } from 'react-toastify';

import { useFileImportApiCall } from '../../hooks/useFileImportApiCall';
import { useImportDatabases } from '../../hooks/useImportDatabases';
import { useMaxIpsosId } from '../../hooks/useMaxIpsosId';
import useQreId from '../../hooks/useQreId';
import { defineStyles } from '../../theme';
import { DialogInfo, DialogInfoType } from '../DialogInfo/DialogInfo';
import { ExternalLinkHint } from '../ExternalLinkHint';

const styles = defineStyles({
  root: {
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    pt: 2,
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    mt: 2,
  },
  fileSelectSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 2,
    border: '1px dashed',
    borderColor: 'divider',
    borderRadius: 2,
    backgroundColor: 'action.hover',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: 'primary.main',
      backgroundColor: 'action.selected',
    },
  },
  fileSelectButton: {
    alignSelf: 'flex-start',
    mt: 2,
  },
  fileName: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mt: 1,
    color: 'text.secondary',
    fontSize: '0.875rem',
  },
  errorAlert: {
    mt: 1,
  },
  actions: {
    px: 3,
    pb: 2,
    gap: 2,
  },
  parkingSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.5,
  },
  parkingLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  parkingSelect: {
    minWidth: 200,
    '& .MuiSelect-select': {
      py: 1,
      px: 1.5,
      fontSize: '0.8125rem',
    },
  },
  successAlert: {
    alignItems: 'center',
    mt: 1,
  },
});

export type ImportDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function ImportDialog({ open, onClose }: ImportDialogProps) {
  const { qreId } = useQreId();
  const fileImportApiCall = useFileImportApiCall(qreId);
  const notificationManager = useNotificationManager();
  const importDatabasesApiCall = useImportDatabases(qreId);
  const intl = useIntl();
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  const { data: maxIpsosId } = useMaxIpsosId(qreId, open);

  // Fetch import databases from API
  const { data: databaseOptions } = useQuery({
    queryKey: ['importDatabases', qreId],
    queryFn: () => importDatabasesApiCall(),
  });

  // Array of keys for Autocomplete options (we store keys, display values)
  const databaseKeys = Object.keys(databaseOptions || {}) as string[];
  const typedDatabaseOptions = databaseOptions as
    | Record<
        string,
        {
          name: string;
          enabled: boolean;
          prefillColumns?: Array<{
            name: string;
            required: boolean;
            enabled: boolean;
            defaultValue?: string;
          }>;
          additionalRequiredHeaders?: string[];
        }
      >
    | undefined;

  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: uploadAsync, isPending } = useMutation<
    { operationId?: string },
    Error,
    { file: File; database: string; prefillColumns?: Record<string, string>; parking: string }
  >({
    retry: false,
    mutationFn: async ({ file, database, prefillColumns, parking }) => {
      const result = await fileImportApiCall(file, database, prefillColumns, parking);
      return result;
    },
  });

  const { register, resetField, setValue, control, watch, handleSubmit } = useForm<{
    file: File | null;
    database: string | null;
    prefillValues: Record<string, string>;
    parking: string;
  }>({
    defaultValues: { file: null, database: null, prefillValues: {}, parking: '0' },
  });

  const fileInput = register('file');
  const selectedFile = watch('file');
  const selectedDatabase = watch('database');

  const prefillColumns = useMemo(() => {
    return selectedDatabase ? typedDatabaseOptions?.[selectedDatabase]?.prefillColumns : undefined;
  }, [selectedDatabase, typedDatabaseOptions]);

  const additionalRequiredHeaders = useMemo(() => {
    return selectedDatabase ? typedDatabaseOptions?.[selectedDatabase]?.additionalRequiredHeaders : undefined;
  }, [selectedDatabase, typedDatabaseOptions]);

  // Set default values when database or prefillColumns change
  useEffect(() => {
    if (prefillColumns) {
      prefillColumns.forEach((column) => {
        if (column.defaultValue !== undefined) {
          setValue(`prefillValues.${column.name}`, column.defaultValue, { shouldValidate: false });
        }
      });
    }
  }, [prefillColumns, setValue]);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const maybeFile = e.target?.files?.[0];
      const reader = new FileReader();

      if (maybeFile) {
        reader.onload = function (e) {
          const CSVBlob = new Blob([new Uint8Array(e.target?.result as ArrayBuffer)], { type: maybeFile.type });
          const CSVFile = new File([CSVBlob], maybeFile.name, { type: maybeFile.type });
          setValue('file', CSVFile, { shouldValidate: true });
          // Clear error when user selects a new file
          setError(null);
        };
        reader.readAsArrayBuffer(maybeFile);
      }
    },
    [setValue]
  );

  const handleClose = useCallback(
    (force?: boolean) => {
      if (!force && (isPending || isProcessing)) {
        return;
      }

      resetField('file');
      resetField('database');
      resetField('prefillValues');
      resetField('parking');
      setError(null);
      setIsProcessing(false);
      onClose();
    },
    [resetField, onClose, isPending, isProcessing]
  );

  const onSubmit = useCallback(
    async (data: {
      file: File | null;
      database: string | null;
      prefillValues: Record<string, string>;
      parking: string;
    }) => {
      // Clear previous error
      setError(null);
      setIsVerified(false);

      if (!data.file) {
        const errorMessage = <FormattedMessage id="upload.noFileSelected.text" defaultMessage="Please select a file" />;
        setError(errorMessage);

        return;
      }
      if (!data.database) {
        const errorMessage = (
          <FormattedMessage id="upload.noDatabaseSelected.text" defaultMessage="Please select a database" />
        );
        setError(errorMessage);

        return;
      }

      try {
        const prefillColumns = isNotNilOrEmpty(data.prefillValues) ? data.prefillValues : undefined;

        const response = await uploadAsync({
          file: data.file,
          database: data.database!,
          prefillColumns,
          parking: data.parking,
        });

        if (response?.operationId && notificationManager) {
          setIsProcessing(true);
          notificationManager.trackOperation({
            operationId: response.operationId,
            processingToast: <FormattedMessage id="upload.verifyingMessage.text" defaultMessage="Verifying file..." />,
            successToast: (status: OperationStatus) => (
              <>
                <strong>
                  <FormattedMessage
                    id="upload.uploadSuccessMessage.text"
                    defaultMessage="Data uploaded successfully!"
                  />
                </strong>
                <br />
                <FormattedMessage
                  id="upload.rowsProcessed.text"
                  defaultMessage="{processed} rows processed"
                  values={{ processed: status?.totalCount }}
                />
                <br />
                <br />
                <FormattedMessage
                  id="upload.created.text"
                  defaultMessage="{created} created"
                  values={{ created: status?.processed?.created ?? 0 }}
                />
                <br />
                <FormattedMessage
                  id="upload.exists.text"
                  defaultMessage="{exists} already existed"
                  values={{ exists: status?.processed?.exists ?? 0 }}
                />
                <br />
                <FormattedMessage
                  id="upload.existed.text"
                  defaultMessage="{existed} existed before"
                  values={{ existed: status?.processed?.existed ?? 0 }}
                />
                <br />
              </>
            ),
            onVerified: () => {
              toast(
                <FormattedMessage
                  id="importDialog.success"
                  defaultMessage="File is successfully validated and is being processed into the background. You can import another file meanwhile."
                />,
                { type: 'info' }
              );
              handleClose(true);
            },
          });
        } else {
          handleClose();
        }
      } catch (error) {
        setError(<FormattedMessage {...(error as MessageDescriptor)} />);
      }
    },
    [uploadAsync, notificationManager, handleClose]
  );

  const handleUserClose = useCallback(() => handleClose(), [handleClose]);

  return (
    <Dialog open={open} onClose={handleUserClose} maxWidth="sm" fullWidth sx={styles.root}>
      <DialogTitle sx={styles.header}>
        <Box sx={styles.headerText}>
          <FormattedMessage id="upload.dialog.title" defaultMessage="Import Data" />
        </Box>

        <ButtonBase onClick={handleUserClose} disableRipple>
          <Icon path={mdiClose} size={1} />
        </ButtonBase>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <form id="import-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={styles.formRow}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage
                id="upload.import.description"
                defaultMessage="Zde můžete importovat nové záznamy - všechny řádky s novým ipsosId přidáme. Pokud potřebujete aktualizovat již importované řádky, zvolte prosím CSV Update."
              />
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <FormattedMessage
                id="upload.maxIpsosId.caption"
                defaultMessage="Aktuální nejvyšší IpsosId: {value}"
                values={{ value: maxIpsosId ?? '0' }}
              />
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              <FormattedMessage id="upload.fileSection.title" defaultMessage="CSV File" />
            </Typography>

            <ExternalLinkHint />
            <Box sx={styles.fileSelectSection}>
              <DialogInfo type={DialogInfoType.IMPORT} />
              <Button
                variant="outlined"
                component="label"
                sx={styles.fileSelectButton}
                startIcon={<Icon path={mdiFileDocument} size={1} />}
              >
                <FormattedMessage id="upload.selectFile.button" defaultMessage="Select File" />
                <input {...fileInput} type={'file'} accept={'.xlsx,.csv'} hidden onChange={handleFileSelect} />
              </Button>
              {selectedFile && (
                <Typography sx={styles.fileName}>
                  <Icon path={mdiFileDocument} size={0.875} />
                  <FormattedMessage
                    id="upload.selectedFile.text"
                    defaultMessage="Selected: {fileName}"
                    values={{ fileName: selectedFile.name }}
                  />
                </Typography>
              )}
            </Box>
          </Box>

          {additionalRequiredHeaders && (
            <Alert severity="warning" sx={styles.errorAlert}>
              <FormattedMessage
                id="upload.additionalRequiredHeaders.text"
                defaultMessage="Pro tuto databázi jsou povinné také sloupce {headers}"
                values={{
                  headers: <strong>{additionalRequiredHeaders.join(', ')}</strong>,
                }}
              />
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={styles.errorAlert}>
              {error}
            </Alert>
          )}

          <Box sx={styles.formRow}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              <FormattedMessage id="upload.databaseSection.title" defaultMessage="Database" />
            </Typography>
            <Controller
              name="database"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={databaseKeys}
                  getOptionLabel={(option) => typedDatabaseOptions?.[option]?.name || option}
                  getOptionDisabled={(option) => !typedDatabaseOptions?.[option]?.enabled}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={intl.formatMessage({ id: 'upload.database.label', defaultMessage: 'Database' })}
                      placeholder={intl.formatMessage({
                        id: 'upload.database.placeholder',
                        defaultMessage: 'Select or type database',
                      })}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon path={mdiDatabase} size={1} style={{ opacity: 0.6 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                    // Clear error when user changes database selection
                    setError(null);
                  }}
                  value={field.value || null}
                />
              )}
            />
          </Box>

          {isNotNilOrEmpty(prefillColumns) && (
            <Box sx={styles.formRow}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                <FormattedMessage id="upload.prefillColumns.title" defaultMessage="Prefill Columns" />
              </Typography>
              {prefillColumns?.map((column) => (
                <Controller
                  key={column.name}
                  name={`prefillValues.${column.name}`}
                  control={control}
                  defaultValue={column.defaultValue || ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={column.name}
                      fullWidth
                      variant="outlined"
                      value={field.value || ''}
                      required={column.required}
                      disabled={!column.enabled}
                    />
                  )}
                />
              ))}
            </Box>
          )}

          <Box sx={styles.parkingSection}>
            <Box sx={styles.parkingLabel}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                <FormattedMessage id="upload.parkingSection.title" defaultMessage="Parking" />
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                <FormattedMessage
                  id="upload.parking.hint"
                  defaultMessage="Hodnota sloupce 'Parking' v souboru má přednost"
                />
              </Typography>
            </Box>
            <Controller
              name="parking"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={styles.parkingSelect} variant="outlined">
                  <Select {...field} value={field.value} onChange={(e) => field.onChange(e.target.value)} displayEmpty>
                    <MenuItem value="0">
                      0 - <FormattedMessage id="upload.parking.disabledDescription" defaultMessage="Zařadit k volání" />
                    </MenuItem>
                    <MenuItem value="1">
                      1 -{' '}
                      <FormattedMessage id="upload.parking.enabledDescription" defaultMessage="Zařadit na parkoviště" />
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          {isVerified && (
            <Alert severity="success" sx={styles.successAlert}>
              <FormattedMessage
                id="importDialog.success"
                defaultMessage="File is successfully validated and is being processed into the background. You can import another file meanwhile."
              />
            </Alert>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={handleUserClose} variant="outlined">
          <FormattedMessage id="common.cancel.button" defaultMessage="Cancel" />
        </Button>
        <LoadingButton
          type="submit"
          form="import-form"
          variant="contained"
          loading={isPending || isProcessing}
          startIcon={<Icon path={mdiUpload} size={1} />}
        >
          <FormattedMessage id="upload.import.button" defaultMessage="Import" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
